import groq from "groq";
import { handleClientError } from "../../errors";
import { getClient } from "../sanity";
import { type BlogSimple, blogSimpleFragment } from "./blogs";
import { type IntegrationSimple, integrationSimpleFragment } from "./integrations";

export type ExternalArticleProps = {
  url: string;
  eyebrow: string;
  title: string;
  description: string;
  publishedAt: string;
  imageUrl: string;
};

export type YoutubeProps = {
  url: string;
  title: string;
  description: string;
  uploadDate: string;
  imageUrl: string;
};

export type AnalyticsToolProps = {
  name: string;
  url: string;
  imageUrl: string;
};

export type ServicePartnerProps = {
  name: string;
  url: string;
  tier: string;
  imageUrl: string;
};

export type ComposableCdpProps = {
  eyebrow: string;
  title: string;
  description: string;
  externalArticles: ExternalArticleProps[];
  youtubeVideos: YoutubeProps[];
  analyticsTools: AnalyticsToolProps[];
  servicePartners: ServicePartnerProps[];
  sources: IntegrationSimple[];
  destinations: IntegrationSimple[];
  blogs: BlogSimple[];
  metaTitle: string;
  metaDescription?: string;
  metaImage?: string;
};

export const getComposableCdpHub = async (): Promise<ComposableCdpProps | undefined> => {
  const query = groq`
    *[_type == "composableCdpHub"] {
      eyebrow,
      title,
      description,
      metaTitle,
      metaDescription,
      'metaImage': metaImage.asset->url,
      externalArticles[]{ url, eyebrow, title, description, publishedAt, 'imageUrl': image.asset->url },
      youtubeVideos[]{ url, title, description, uploadDate, 'imageUrl': image.asset->url },
      analyticsTools[]{ name, url, 'imageUrl': logo.asset->url },
      servicePartners[]{ name, url, tier, 'imageUrl': logo.asset->url },
      sources[]->{ ${integrationSimpleFragment} },
      destinations[]->{ ${integrationSimpleFragment} },
      blogs[]->{ ${blogSimpleFragment} },
    }
  `;

  try {
    const composableCdp = await getClient().fetch(query);
    return composableCdp[0] ?? undefined;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch composable CDP hub: ${message}`);
    return undefined;
  }
};
